/**
 * Error handling utilities
 * Provides user-friendly error messages for different error types
 */

/**
 * Formats an error into a user-friendly message
 * @param {Error} error - The error object from axios or fetch
 * @returns {string} - User-friendly error message
 */
export function formatErrorMessage(error) {
  // Handle network errors (API server not running, no internet, etc.)
  if (!error.response) {
    // Check for specific network error codes
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
      return 'Unable to connect to the API. In development, ensure zra-api is running on port 4100 (or set VITE_PROXY_TARGET to match the API port).';
    }
    
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return 'Request timed out. The server may be slow or unavailable. Please try again.';
    }
    
    if (error.request) {
      // Check if it's a CORS error (often indicates server is down or misconfigured)
      if (error.message?.includes('CORS') || error.message?.includes('Failed to fetch')) {
        return 'Unable to connect to the server. Please ensure the API server is running and CORS is properly configured.';
      }
      return 'Network error. Please check your internet connection and try again.';
    }
    
    // Generic network error
    return 'Unable to reach the server. Please ensure the API server is running and try again.';
  }
  
  // Handle HTTP status codes
  const status = error.response?.status;
  const statusText = error.response?.statusText;
  const errorData = error.response?.data;
  
  // Try to get a user-friendly message from the API response
  if (errorData?.errors?.length) {
    const joined = errorData.errors
      .map((e) => (typeof e === 'string' ? e : e?.message))
      .filter(Boolean)
      .join('. ');
    if (joined) return joined;
  }

  if (errorData?.message) {
    return errorData.message;
  }
  
  // Provide specific messages for common status codes
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input and try again.';
    case 401:
      return 'Authentication required. Please log in again.';
    case 403:
      return 'You do not have permission to access this resource.';
    case 404:
      return 'The requested resource was not found.';
    case 500:
      // 500 often occurs when the dev proxy cannot reach the API (ECONNREFUSED)
      return 'The server could not complete your request. In development, ensure zra-api is running on port 4100 (or set VITE_PROXY_TARGET in the frontend to match the API port).';
    case 502:
      return 'The API could not be reached (bad gateway). Ensure zra-api is running on port 4100 and the proxy target matches.';
    case 503:
      if (errorData?.error === 'DATABASE_UNAVAILABLE') {
        return (
          errorData.message ||
          'Database is unavailable. Check that PostgreSQL is running and the API connection settings are correct.'
        );
      }
      return 'Service unavailable. The server is temporarily down for maintenance. Please try again later.';
    case 504:
      return 'Gateway timeout. The server took too long to respond. Please try again.';
    default:
      // Fallback to status text or generic message
      if (statusText) {
        return `Error ${status}: ${statusText}`;
      }
      return 'An unexpected error occurred. Please try again.';
  }
}

/**
 * Determines if an error is a connection/network error
 * @param {Error} error - The error object
 * @returns {boolean} - True if it's a network/connection error
 */
export function isConnectionError(error) {
  if (!error.response) {
    return true;
  }
  
  // 502, 503, 504 are often gateway/proxy errors that indicate server issues
  const status = error.response?.status;
  return status === 502 || status === 503 || status === 504;
}

/**
 * Determines if an error is a server error (5xx)
 * @param {Error} error - The error object
 * @returns {boolean} - True if it's a server error
 */
export function isServerError(error) {
  const status = error.response?.status;
  return status >= 500 && status < 600;
}

/**
 * Determines if an error is a client error (4xx)
 * @param {Error} error - The error object
 * @returns {boolean} - True if it's a client error
 */
export function isClientError(error) {
  const status = error.response?.status;
  return status >= 400 && status < 500;
}

/**
 * Gets error severity level for UI styling
 * @param {Error} error - The error object
 * @returns {'error'|'warning'|'info'} - Error severity
 */
export function getErrorSeverity(error) {
  if (isConnectionError(error)) {
    return 'error';
  }
  
  if (isServerError(error)) {
    return 'error';
  }
  
  if (isClientError(error)) {
    const status = error.response?.status;
    if (status === 401 || status === 403) {
      return 'warning';
    }
    return 'info';
  }
  
  return 'error';
}

/**
 * Reason text for a failed request: API message, or status / code / HTML body when the API sent none.
 * @param {Error & { response?: { status?: number, data?: unknown }, code?: string }} error
 * @param {string} [fallback]
 */
export function describeApiError(error, fallback = 'Request failed') {
  const data = error?.response?.data;
  const status = error?.response?.status;
  let bodyMessage = '';
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const record = /** @type {{ message?: unknown, error?: unknown }} */ (data);
    if (typeof record.message === 'string' && record.message.trim()) bodyMessage = record.message.trim();
    else if (typeof record.error === 'string' && record.error.trim()) bodyMessage = record.error.trim();
  } else if (typeof data === 'string') {
    const text = data.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    if (text) bodyMessage = text.slice(0, 400);
  }

  const formatted = formatErrorMessage(error || {});
  const lines = [];
  if (bodyMessage) lines.push(bodyMessage);
  else if (formatted) lines.push(formatted);

  const extras = [];
  if (status) extras.push(`HTTP ${status}`);
  if (error?.code) extras.push(String(error.code));
  if (extras.length) lines.push(extras.join(' · '));

  const unique = [];
  for (const line of lines) {
    if (line && !unique.includes(line)) unique.push(line);
  }
  return unique.join('\n') || fallback;
}

export default {
  formatErrorMessage,
  describeApiError,
  isConnectionError,
  isServerError,
  isClientError,
  getErrorSeverity,
};
